/**
 * 画布编辑器（P1 C-2b）：@xyflow/react 渲染 store 的节点/连线，支持拖动与拉线连接。
 *
 * 数据流：进入时把 canvasStore.doc 映射为 xyflow 的本地 nodes/edges（xyflow 需要本地态
 * 驱动拖拽）；拖拽结束 → 回写 store.moveNode；拉线 → store.connect。store 仍是持久化的真源，
 * xyflow 本地态只是编辑期的视图镜像。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Background,
  BackgroundVariant,
  MiniMap,
  Panel,
  ReactFlow,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import type { Connection, Edge, Node, ReactFlowInstance } from '@xyflow/react';
import { ArrowLeft, Library, Loader2, Lock, Maximize, Plus, Redo2, Undo2, Unlock, Wand2, ZoomIn, ZoomOut } from 'lucide-react';

import {
  NODE_KIND_LABELS,
  createImageGenNode,
  createNode,
  createVideoGenNode,
  createAudioNode,
  createGroupNode,
  isAudioPayload,
  isGroupPayload,
  updateAudioLine,
  GROUP_COLORS,
  type AudioLine,
  type AudioPayload,
  type CanvasNode,
  type GenerationParams,
  type GroupPayload,
  type MediaRef,
  type NodeKind,
} from '../../model/canvas';
import {
  composeImagePrompt,
  composeImagePromptFromShot,
  composeVideoPrompt,
  createTextGenNode,
  createShotNode,
  defaultNegativePrompt,
  isLiteraryScriptResult,
  isShotPayload,
  isStoryboardResult,
  isTextGenPayload,
  parseDialogueLines,
  patchShot,
  previewOfPayload,
  shotLabel,
  type GeneratedPayload,
  type LiteraryScript,
  type ShotPayload,
  type StoryboardShot,
  type TextGenCapability,
  type TextGenPayload,
} from '../../model/generation';
import {
  SUBJECT_CATEGORY_LABELS,
  createSubjectNode,
  extractEntities,
  isSubjectPayload,
  subjectPrompt,
  turnaroundPrompts,
  attachViewsToLook,
  ensureLooks,
  type SubjectCategory,
  type SubjectPayload,
} from '../../model/subject';
import { GenResultEditor } from '../../features/canvas/GenResultEditor';
import { ImageNodePanel } from '../../features/canvas/ImageNodePanel';
import { SubjectNodePanel } from '../../features/canvas/SubjectNodePanel';
import { ShotNodePanel } from '../../features/canvas/ShotNodePanel';
import { VideoNodePanel, AudioNodePanel } from '../../features/canvas/MediaNodePanel';
import { CanvasNodeCard } from '../../features/canvas/CanvasNodeCard';
import { GroupNodeCard } from '../../features/canvas/GroupNodeCard';
import type { CanvasFlowNodeData } from '../../features/canvas/CanvasNodeCard';
import { FlowEdge } from '../../features/canvas/FlowEdge';
import { sceneNegative } from '../../model/prompts';
import { SCRIPT_SKILLS, applySkillSnippet } from '../../model/skills';
import { defaultVoice, type VoiceProfile } from '../../model/voice';
import { generationProvider, type ImageEditOp, type VideoToolOp } from '../../features/canvas/generationService';
import { nextNodeId, useCanvasStore } from '../../features/canvas/canvasStore';
import {
  deleteTemplate,
  gridLayout,
  listTemplates,
  loadTemplate,
  remapTemplate,
  saveTemplate,
  type TemplateMeta,
} from '../../features/canvas/canvasLibrary';
import { TemplatesModal } from '../../features/canvas/TemplatesModal';
import { DrawDialog } from '../../features/canvas/DrawDialog';
import { FloatingPanel } from '../../ui/FloatingPanel';

const nodeTypes = { canvasNode: CanvasNodeCard, groupCard: GroupNodeCard };
const edgeTypes = { flow: FlowEdge };

function hintOf(n: CanvasNode): string | undefined {
  const p = n.payload;
  if (typeof p === 'object' && p !== null) {
    const rec = p as Record<string, unknown>;
    const h = rec.hint ?? rec.text;
    if (typeof h === 'string') {
      return h;
    }
  }
  return undefined;
}

function toFlowNode(n: CanvasNode): Node {
  if (isGroupPayload(n.payload)) {
    const g = n.payload;
    return {
      id: n.id,
      position: { x: n.position.x, y: n.position.y },
      type: 'groupCard',
      zIndex: -1,
      data: { label: g.label, color: g.color, width: g.width, height: g.height, memberCount: g.memberIds.length },
    };
  }
  const media = n.media;
  // 缩略优先用 thumbnail；图像无独立缩略时退回自身 url（视频必须有缩略才显）
  const thumb = media?.thumbnail ?? (n.kind === 'image' ? media?.url : undefined);
  const data: CanvasFlowNodeData = {
    title: n.title,
    kind: n.kind,
    mode: n.mode,
    hint: hintOf(n),
    thumb,
    full: media?.url,
    durationSec: media?.durationSec,
    preview: previewOfPayload(n.payload),
  };
  return { id: n.id, position: { x: n.position.x, y: n.position.y }, type: 'canvasNode', data };
}

/** 按台词角色名回溯其角色卡绑定音色（音频卡继承，A 决策）。模块级、供 handler 与渲染共用。 */
function roleVoiceByName(role: string): VoiceProfile | undefined {
  const doc = useCanvasStore.getState().doc;
  for (const n of doc.nodes) {
    if (isSubjectPayload(n.payload) && n.payload.category === 'character' && n.payload.name === role) {
      return n.payload.voice;
    }
  }
  return undefined;
}

/** 本地文件→data-URI（拖拽导入建节点用）。 */
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('read failed'));
    r.readAsDataURL(file);
  });
}

/** 触发一个 data-URI/URL 下载到本地（批量下载用）。 */
function downloadDataUrl(url: string, name: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = name.replace(/[\\/:*?"<>|]/g, '_') || 'asset';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('img load failed'));
    el.src = src;
  });
}

/** 客户端按比例中心裁切（真实可用，不依赖后端）。 */
async function cropDataUrlToRatio(dataUrl: string, ratio: string): Promise<string> {
  const img = await loadImage(dataUrl);
  const [rw, rh] = ratio.split(':').map(Number);
  const target = rw !== undefined && rh !== undefined && rw > 0 && rh > 0 ? rw / rh : 1;
  let sw = img.naturalWidth;
  let sh = img.naturalHeight;
  if (sw / sh > target) {
    sw = Math.round(sh * target);
  } else {
    sh = Math.round(sw / target);
  }
  const sx = Math.round((img.naturalWidth - sw) / 2);
  const sy = Math.round((img.naturalHeight - sh) / 2);
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    return dataUrl;
  }
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  return canvas.toDataURL('image/png');
}

/** 灯光预置方位 → 光源中心坐标（画布像素）。 */
function lightPos(preset: string, w: number, h: number): { x: number; y: number } {
  const m = 0.2;
  if (preset === '左上') return { x: w * m, y: h * m };
  if (preset === '右上') return { x: w * (1 - m), y: h * m };
  if (preset === '左下') return { x: w * m, y: h * (1 - m) };
  if (preset === '右下') return { x: w * (1 - m), y: h * (1 - m) };
  if (preset === '逆光') return { x: w / 2, y: h * m };
  return { x: w / 2, y: h / 2 };
}

/** 客户端径向叠加暖光（真实可用，不依赖后端）。 */
async function relightImage(dataUrl: string, preset: string): Promise<string> {
  const img = await loadImage(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    return dataUrl;
  }
  ctx.drawImage(img, 0, 0);
  const pos = lightPos(preset, canvas.width, canvas.height);
  const g = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, Math.max(canvas.width, canvas.height) * 0.65);
  g.addColorStop(0, 'rgba(255,238,204,0.6)');
  g.addColorStop(1, 'rgba(255,238,204,0)');
  ctx.globalCompositeOperation = 'screen';
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.globalCompositeOperation = 'source-over';
  return canvas.toDataURL('image/png');
}

const ctlBtn =
  'flex h-8 w-8 items-center justify-center text-ink-secondary transition-colors hover:bg-surface-raised hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40';

const menuItem =
  'w-full rounded-control px-2 py-1 text-left text-ui text-ink-secondary transition-colors hover:bg-surface-raised hover:text-ink-primary';
const menuItemDanger =
  'w-full rounded-control px-2 py-1 text-left text-ui text-ink-secondary transition-colors hover:bg-surface-raised hover:text-state-danger';

/** 右键菜单目标（C-3b）：node/edge 带 id，pane 无 */
type MenuState =
  | { kind: 'node'; x: number; y: number; id: string }
  | { kind: 'edge'; x: number; y: number; id: string }
  | { kind: 'pane'; x: number; y: number };

interface CanvasControlsProps {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  locked: boolean;
  onToggleLock: () => void;
}

/** 左侧居中的竖排控件组：缩放/适应/锁定 + 撤销/重做（用户 2026-10-01：与缩放锁定放一起、别沉左下角）。
 *  作为 ReactFlow 子组件渲染，故可用 useReactFlow 拿 zoomIn/zoomOut/fitView。 */
function CanvasControls({ canUndo, canRedo, onUndo, onRedo, locked, onToggleLock }: CanvasControlsProps) {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  return (
    <Panel position="center-left" className="!m-0">
      <div className="flex flex-col overflow-hidden rounded-control border border-surface-line bg-surface-overlay shadow-[0_2px_10px_rgba(0,0,0,.4)]">
        <button type="button" className={ctlBtn} title="放大" onClick={() => zoomIn()}>
          <ZoomIn size={16} />
        </button>
        <button type="button" className={ctlBtn} title="缩小" onClick={() => zoomOut()}>
          <ZoomOut size={16} />
        </button>
        <button type="button" className={ctlBtn} title="适应视图" onClick={() => fitView()}>
          <Maximize size={16} />
        </button>
        <button
          type="button"
          className={ctlBtn}
          title={locked ? '解锁交互' : '锁定交互'}
          aria-pressed={locked}
          onClick={onToggleLock}
        >
          {locked ? <Lock size={16} /> : <Unlock size={16} />}
        </button>
        <div className="h-px w-full bg-surface-line" />
        <button type="button" className={ctlBtn} title="撤销 (Ctrl+Z)" disabled={!canUndo} onClick={onUndo}>
          <Undo2 size={16} />
        </button>
        <button type="button" className={ctlBtn} title="重做 (Ctrl+Y)" disabled={!canRedo} onClick={onRedo}>
          <Redo2 size={16} />
        </button>
      </div>
    </Panel>
  );
}

export function CanvasEditorPage() {
  const navigate = useNavigate();
  const doc = useCanvasStore((s) => s.doc);
  const moveNode = useCanvasStore((s) => s.moveNode);
  const connect = useCanvasStore((s) => s.connect);
  const removeEdge = useCanvasStore((s) => s.removeEdge);
  const removeNode = useCanvasStore((s) => s.removeNode);
  const undo = useCanvasStore((s) => s.undo);
  const redo = useCanvasStore((s) => s.redo);
  const setTitle = useCanvasStore((s) => s.setTitle);
  const canUndo = useCanvasStore((s) => s._past.length > 0);
  const canRedo = useCanvasStore((s) => s._future.length > 0);
  const [addOpen, setAddOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  // 画布名可编辑：点标题进入编辑，回车/失焦提交（走 store.setTitle，随之持久化）
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(doc.title);
  const addRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // 锁定交互：开启后禁拖拽/平移/缩放/选中（左侧控件组里的锁图标控制）
  const [locked, setLocked] = useState(false);
  // 右键上下文菜单（C-3b）：node/edge/pane 三类目标，坐标为视口 clientX/Y
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const rfRef = useRef<ReactFlowInstance | null>(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates] = useState<TemplateMeta[]>([]);
  const [templateCount, setTemplateCount] = useState(0);
  const [drawTarget, setDrawTarget] = useState<CanvasNode | null>(null);

  useEffect(() => {
    setTemplateCount(listTemplates().length);
  }, []);
  const rawMenuPos = useRef({ x: 0, y: 0 });

  const initialNodes = useMemo(() => doc.nodes.map(toFlowNode), [doc.nodes]);
  const initialEdges = useMemo<Edge[]>(
    () => doc.edges.map((e) => ({ id: e.id, source: e.from, target: e.to, type: 'flow' })),
    [doc.edges],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [status, setStatus] = useState<string | null>(null);
  // 文本生成中的节点 id（瞬时 UI 态，不落盘）
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  // 生成浮窗的初始视口坐标（由选中节点的 DOM 位置算出→面板跟随节点）
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);

  // 激活边：自身被选中，或任一端节点被选中（用户：选中卡片→其上下游连线变蓝+流动）
  const selectedNodeIds = useMemo(
    () => new Set(nodes.filter((n) => n.selected).map((n) => n.id)),
    [nodes],
  );
  const visibleEdges = useMemo<Edge[]>(
    () =>
      edges.map((e) => ({
        ...e,
        data: {
          ...(e.data ?? {}),
          active: Boolean(e.selected) || selectedNodeIds.has(e.source) || selectedNodeIds.has(e.target),
        },
      })),
    [edges, selectedNodeIds],
  );

  const onConnect = useCallback(
    (conn: Connection) => {
      if (conn.source === null || conn.target === null) {
        return;
      }
      if (connect(conn.source, conn.target)) {
        setEdges((eds) =>
          addEdge({ ...conn, id: `e${eds.length + 1}`, type: 'flow' }, eds),
        );
        setStatus('已连接');
      } else {
        setStatus('无法连接（自环、重复或端点无效）');
      }
    },
    [connect, setEdges],
  );

  const onNodeDragStop = useCallback(
    (_e: unknown, node: Node) => {
      moveNode(node.id, { x: node.position.x, y: node.position.y });
    },
    [moveNode],
  );

  // 删除回写 store（否则刷新后从 store 又长回来）：删节点时 store 会级联删其连线
  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      for (const e of deleted) {
        removeEdge(e.id);
      }
      setStatus(`已删除 ${deleted.length} 条连线`);
    },
    [removeEdge],
  );
  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      for (const n of deleted) {
        removeNode(n.id);
      }
      setStatus(`已删除 ${deleted.length} 个节点`);
    },
    [removeNode],
  );

  /** 添加节点：同时写 store（持久）与 xyflow 本地态（即时可见）；无 at 则阶梯摆放避免重叠 */
  const handleAdd = useCallback(
    (kind: NodeKind, at?: { x: number; y: number }) => {
      const s = useCanvasStore.getState();
      const id = nextNodeId(s.doc.nodes.map((n) => n.id));
      const count = s.doc.nodes.length;
      const pos = at ?? { x: 80 + (count % 12) * 44, y: 80 + (count % 12) * 44 };
      const node =
        kind === 'image'
          ? createImageGenNode(id, pos)
          : kind === 'subject'
            ? createSubjectNode('character', id, pos)
            : createNode(kind, id, pos);
      if (s.addNode(node)) {
        setNodes((ns) => [...ns, toFlowNode(node)]);
        setStatus(`已添加 ${NODE_KIND_LABELS[kind]} 节点`);
      }
      setAddOpen(false); // 选完即收起下拉（D-49 反馈 1）
      setMenu(null);
    },
    [setNodes],
  );

  /** 添加文本生成节点（仅文学剧本；导演剧本由拆分自动派生）：同 handleAdd 一样双写 store + 本地态 */
  const addTextNode = useCallback(
    (capability: TextGenCapability, at?: { x: number; y: number }) => {
      const s = useCanvasStore.getState();
      const id = nextNodeId(s.doc.nodes.map((n) => n.id));
      const count = s.doc.nodes.length;
      const pos = at ?? { x: 80 + (count % 12) * 44, y: 80 + (count % 12) * 44 };
      const node = createTextGenNode(capability, id, pos);
      if (s.addNode(node)) {
        setNodes((ns) => [...ns, toFlowNode(node)]);
        setStatus(capability === 'textToScript' ? '已添加 文学剧本 节点' : '已添加 导演剧本 节点');
      }
      setAddOpen(false);
      setMenu(null);
    },
    [setNodes],
  );

  /** 添加主体资产卡（角色/场景/道具）：类别建卡即定、卡内只读 */
  const addSubjectNode = useCallback(
    (category: SubjectCategory, at?: { x: number; y: number }) => {
      const s = useCanvasStore.getState();
      const id = nextNodeId(s.doc.nodes.map((n) => n.id));
      const count = s.doc.nodes.length;
      const pos = at ?? { x: 80 + (count % 12) * 44, y: 80 + (count % 12) * 44 };
      const node = createSubjectNode(category, id, pos);
      if (s.addNode(node)) {
        setNodes((ns) => [...ns, toFlowNode(node)]);
        setStatus(`已添加 ${SUBJECT_CATEGORY_LABELS[category]} 资产卡`);
      }
      setAddOpen(false);
      setMenu(null);
    },
    [setNodes],
  );

  /** 把 store 当前 doc 推回 xyflow 本地态（撤销/重做后同步视图）。
   *  必须显式 setNodes/setEdges：nodes/edges 是页面级 useNodesState 持有，
   *  仅靠 key 重挂 ReactFlow 子树不会重置父级 hook，会留下旧边旧节点。 */
  const syncFromDoc = useCallback(() => {
    const d = useCanvasStore.getState().doc;
    setNodes(d.nodes.map(toFlowNode));
    setEdges(d.edges.map((e) => ({ id: e.id, source: e.from, target: e.to, type: 'flow' })));
  }, [setNodes, setEdges]);

  /** 撤销/重做：先改 store，再把新 doc 同步回本地态 */
  const doUndo = useCallback(() => {
    undo();
    syncFromDoc();
    setStatus('已撤销');
  }, [undo, syncFromDoc]);
  const doRedo = useCallback(() => {
    redo();
    syncFromDoc();
    setStatus('已重做');
  }, [redo, syncFromDoc]);

  /** 改提示词：写回 store（payload.prompt），面板从 doc 重读——不需同步本地卡面 */
  const setPrompt = useCallback((id: string, payload: TextGenPayload, prompt: string) => {
    useCanvasStore.getState().updateNode(id, { payload: { ...payload, prompt } });
  }, []);

  /** 把 store 里该节点重算成最新 xyflow data（保留选中/位置）：编辑后刷新卡面而不关面板 */
  const refreshNodeData = useCallback(
    (id: string) => {
      const n = useCanvasStore.getState().doc.nodes.find((x) => x.id === id);
      if (n === undefined) {
        return;
      }
      const data = toFlowNode(n).data;
      setNodes((ns) => ns.map((x) => (x.id === id ? { ...x, data } : x)));
    },
    [setNodes],
  );

  /** 写回 result 到 store + 刷新该节点卡面（不清选中，面板不关） */
  const applyResult = useCallback(
    (id: string, payload: TextGenPayload, result: GeneratedPayload | undefined) => {
      useCanvasStore.getState().updateNode(id, { payload: { ...payload, result } });
      refreshNodeData(id);
    },
    [refreshNodeData],
  );

  /** 生成：调 provider（现为 mock）→回填 payload.result */
  const runGenerate = useCallback(
    async (id: string, payload: TextGenPayload) => {
      setGeneratingId(id);
      setStatus('生成中…');
      try {
        const s = useCanvasStore.getState();
        let upstreamScript: LiteraryScript | undefined;
        if (payload.capability === 'scriptToStoryboard') {
          const fromId = s.doc.edges.find((e) => e.to === id)?.from;
          const up = fromId !== undefined ? s.doc.nodes.find((n) => n.id === fromId) : undefined;
          const r = up !== undefined && isTextGenPayload(up.payload) ? up.payload.result : undefined;
          if (isLiteraryScriptResult(r)) {
            upstreamScript = r.script;
          }
        }
        const result = await generationProvider.generate({
          capability: payload.capability,
          prompt: payload.prompt,
          upstreamScript,
        });
        applyResult(id, payload, result);
        setStatus('已生成');
      } catch {
        setStatus('生成失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [applyResult],
  );

  /** 一键优化提示词（现 mock）：改写 payload.prompt */
  const runOptimize = useCallback(async (id: string, payload: TextGenPayload) => {
    setGeneratingId(id);
    setStatus('优化中…');
    try {
      const better = await generationProvider.enhancePrompt(payload.prompt);
      useCanvasStore.getState().updateNode(id, { payload: { ...payload, prompt: better } });
      setStatus('已优化提示词');
    } catch {
      setStatus('优化失败');
    } finally {
      setGeneratingId(null);
    }
  }, []);

  /** 清空已生成结果（保留提示词） */
  const clearResult = useCallback(
    (id: string, payload: TextGenPayload) => {
      applyResult(id, payload, undefined);
      setStatus('已清空结果');
    },
    [applyResult],
  );

  /** 上传已有剧本（仅文学剧本）：读纯文本→填入提示词（自动保存，无需“保存”钮） */
  const onUploadScript = useCallback((id: string, payload: TextGenPayload, file: File) => {
    file
      .text()
      .then((t) => {
        useCanvasStore.getState().updateNode(id, { payload: { ...payload, prompt: t } });
        setStatus('已导入脚本文本');
      })
      .catch(() => setStatus('导入失败'));
  }, []);

  /* ---------- 图像节点（P2-图像） ---------- */
  const patchImageParams = useCallback(
    (id: string, patch: Partial<GenerationParams>) => {
      const n = useCanvasStore.getState().doc.nodes.find((x) => x.id === id);
      if (n === undefined || n.params === undefined) {
        return;
      }
      useCanvasStore.getState().updateNode(id, { params: { ...n.params, ...patch } });
      refreshNodeData(id);
    },
    [refreshNodeData],
  );

  const runGenerateImage = useCallback(
    async (node: CanvasNode) => {
      if (node.params === undefined) {
        return;
      }
      setGeneratingId(node.id);
      setStatus('生成中…');
      try {
        const prompt = node.params.enhancePrompt
          ? await generationProvider.enhancePrompt(node.params.prompt)
          : node.params.prompt;
        const imgs = await generationProvider.generateImage({
          prompt,
          aspectRatio: node.params.aspectRatio,
          resolution: node.params.resolution,
          count: node.params.count,
        });
        const stamp = Date.now().toString(36);
        const medias: MediaRef[] = imgs.map((img, i) => ({
          mediaId: `media-${stamp}-${i}`,
          url: img.url,
          thumbnail: img.thumbnail,
          width: img.width,
          height: img.height,
        }));
        const first = medias[0];
        if (first !== undefined) {
          useCanvasStore.getState().updateNode(node.id, { media: first, variants: medias });
          refreshNodeData(node.id);
          setStatus(`已生成 ${medias.length} 张`);
        }
      } catch {
        setStatus('生成失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [refreshNodeData],
  );

  const runOptimizeImage = useCallback(
    async (node: CanvasNode) => {
      if (node.params === undefined) {
        return;
      }
      setGeneratingId(node.id);
      setStatus('优化中…');
      try {
        const better = await generationProvider.enhancePrompt(node.params.prompt);
        const negative = node.params.negativePrompt !== undefined && node.params.negativePrompt !== '' ? node.params.negativePrompt : defaultNegativePrompt();
        useCanvasStore.getState().updateNode(node.id, { params: { ...node.params, prompt: better, negativePrompt: negative } });
        refreshNodeData(node.id);
        setStatus('已优化提示词');
      } catch {
        setStatus('优化失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [refreshNodeData],
  );

  /** 从候选里选定主图（非破坏：只改 media，不动 variants） */
  const pickVariant = useCallback(
    (id: string, media: MediaRef) => {
      useCanvasStore.getState().updateNode(id, { media });
      refreshNodeData(id);
      setStatus('已选定为主图');
    },
    [refreshNodeData],
  );

  /** 图像编辑（重绘/扩图/抠图）→ 非破坏派生一张新图节点并连线（LibTV 式不覆盖原图） */
  const runImageEdit = useCallback(
    async (node: CanvasNode, op: ImageEditOp) => {
      if (node.params === undefined) {
        return;
      }
      setGeneratingId(node.id);
      setStatus('处理中…');
      try {
        const img = await generationProvider.editImage(op, {
          prompt: node.params.prompt,
          aspectRatio: node.params.aspectRatio,
          resolution: node.params.resolution,
        });
        const s = useCanvasStore.getState();
        const id = nextNodeId(s.doc.nodes.map((n) => n.id));
        const label = op === 'repaint' ? '重绘' : op === 'outpaint' ? '扩图' : '抠图';
        const derived: CanvasNode = {
          id,
          kind: 'image',
          mode: 'derived',
          title: `${label}·${node.title}`,
          position: { x: node.position.x + 260, y: node.position.y + 48 },
          media: {
            mediaId: `media-${Date.now().toString(36)}`,
            url: img.url,
            thumbnail: img.thumbnail,
            width: img.width,
            height: img.height,
          },
        };
        if (s.addNode(derived)) {
          s.connect(node.id, id);
          setNodes((ns) => [...ns.map((n) => ({ ...n, selected: false })), { ...toFlowNode(derived), selected: true }]);
          rfRef.current?.fitView({ nodes: [{ id }], duration: 500, padding: 0.5 });
          setStatus(`已生成${label}派生图`);
        }
      } catch {
        setStatus('处理失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [setNodes],
  );

  /** 反推提示词（P-B1）：从当前图解析 prompt 回填 */
  const runReversePrompt = useCallback(
    async (node: CanvasNode) => {
      if (node.params === undefined || node.media === undefined) {
        return;
      }
      setGeneratingId(node.id);
      setStatus('反推提示词…');
      try {
        const prompt = await generationProvider.reversePrompt({ imageUrl: node.media.url });
        useCanvasStore.getState().updateNode(node.id, { params: { ...node.params, prompt } });
        refreshNodeData(node.id);
        setStatus('已反推提示词');
      } catch {
        setStatus('反推失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [refreshNodeData],
  );

  /** 高清超分（P-B1）：派生一张放大图 */
  const runUpscale = useCallback(
    async (node: CanvasNode) => {
      if (node.media === undefined) {
        return;
      }
      setGeneratingId(node.id);
      setStatus('高清超分…');
      try {
        const img = await generationProvider.upscale({ imageUrl: node.media.url, resolution: '2K' });
        const s = useCanvasStore.getState();
        const id = nextNodeId(s.doc.nodes.map((n) => n.id));
        const derived: CanvasNode = {
          id,
          kind: 'image',
          mode: 'derived',
          title: `高清·${node.title}`,
          position: { x: node.position.x + 260, y: node.position.y + 48 },
          media: { mediaId: `media-${Date.now().toString(36)}`, url: img.url, thumbnail: img.thumbnail, width: img.width, height: img.height },
        };
        if (s.addNode(derived)) {
          s.connect(node.id, id);
          setNodes((ns) => [...ns.map((n) => ({ ...n, selected: false })), { ...toFlowNode(derived), selected: true }]);
          rfRef.current?.fitView({ nodes: [{ id }], duration: 500, padding: 0.5 });
          setStatus('已生成高清派生图');
        }
      } catch {
        setStatus('高清失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [setNodes],
  );

  /** 裁剪（P-B2）：客户端按比例中心裁→派生新图节点 */
  const runCrop = useCallback(
    async (node: CanvasNode, ratio: string) => {
      if (node.media === undefined) {
        return;
      }
      setGeneratingId(node.id);
      setStatus('裁剪中…');
      try {
        const url = await cropDataUrlToRatio(node.media.url, ratio);
        const s = useCanvasStore.getState();
        const id = nextNodeId(s.doc.nodes.map((n) => n.id));
        const derived: CanvasNode = {
          id,
          kind: 'image',
          mode: 'derived',
          title: `裁剪·${ratio}·${node.title}`,
          position: { x: node.position.x + 260, y: node.position.y + 48 },
          media: { mediaId: `media-${Date.now().toString(36)}`, url },
        };
        if (s.addNode(derived)) {
          s.connect(node.id, id);
          setNodes((ns) => [...ns.map((n) => ({ ...n, selected: false })), { ...toFlowNode(derived), selected: true }]);
          rfRef.current?.fitView({ nodes: [{ id }], duration: 500, padding: 0.5 });
          setStatus(`已裁剪为 ${ratio}`);
        }
      } catch {
        setStatus('裁剪失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [setNodes],
  );

  /** 多视角（P-B2）：单图→派生多张视角图节点（连线到本图） */
  const runMultiView = useCallback(
    async (node: CanvasNode) => {
      if (node.media === undefined) {
        return;
      }
      setGeneratingId(node.id);
      setStatus('多视角生成…');
      try {
        const imgs = await generationProvider.multiView({ imageUrl: node.media.url, count: 4 });
        const s = useCanvasStore.getState();
        const stamp = Date.now().toString(36);
        const views: CanvasNode[] = imgs.map((img, i) => ({
          id: `n-${stamp}-mv${i}`,
          kind: 'image',
          mode: 'derived',
          title: `视角${i + 1}·${node.title}`,
          position: { x: node.position.x + 280, y: node.position.y + i * 150 },
          media: { mediaId: `media-${stamp}-${i}`, url: img.url, thumbnail: img.thumbnail, width: img.width, height: img.height },
        }));
        s.spawnViews(node.id, views);
        syncFromDoc();
        setStatus(`已生成 ${views.length} 个视角`);
      } catch {
        setStatus('多视角失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [syncFromDoc],
  );

  /** 灯光（P-B3）：客户端径向暖光叠加→派生新图节点 */
  const runRelight = useCallback(
    async (node: CanvasNode, preset: string) => {
      if (node.media === undefined) {
        return;
      }
      setGeneratingId(node.id);
      setStatus('灯光处理…');
      try {
        const url = await relightImage(node.media.url, preset);
        const s = useCanvasStore.getState();
        const id = nextNodeId(s.doc.nodes.map((n) => n.id));
        const derived: CanvasNode = {
          id,
          kind: 'image',
          mode: 'derived',
          title: `灯光·${preset}·${node.title}`,
          position: { x: node.position.x + 260, y: node.position.y + 48 },
          media: { mediaId: `media-${Date.now().toString(36)}`, url },
        };
        if (s.addNode(derived)) {
          s.connect(node.id, id);
          setNodes((ns) => [...ns.map((n) => ({ ...n, selected: false })), { ...toFlowNode(derived), selected: true }]);
          rfRef.current?.fitView({ nodes: [{ id }], duration: 500, padding: 0.5 });
          setStatus(`已添加${preset}灯光`);
        }
      } catch {
        setStatus('灯光失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [setNodes],
  );

  /** 绘制（P-B3）：打开标注画布 */
  const openDraw = useCallback((node: CanvasNode) => {
    if (node.media !== undefined) {
      setDrawTarget(node);
    }
  }, []);

  /** 绘制应用：合成后的图→派生新图节点 */
  const runApplyDraw = useCallback(
    (node: CanvasNode, dataUrl: string) => {
      const s = useCanvasStore.getState();
      const id = nextNodeId(s.doc.nodes.map((n) => n.id));
      const derived: CanvasNode = {
        id,
        kind: 'image',
        mode: 'derived',
        title: `绘制·${node.title}`,
        position: { x: node.position.x + 260, y: node.position.y + 48 },
        media: { mediaId: `media-${Date.now().toString(36)}`, url: dataUrl },
      };
      if (s.addNode(derived)) {
        s.connect(node.id, id);
        setNodes((ns) => [...ns.map((n) => ({ ...n, selected: false })), { ...toFlowNode(derived), selected: true }]);
        rfRef.current?.fitView({ nodes: [{ id }], duration: 500, padding: 0.5 });
        setStatus('已应用绘制');
      }
      setDrawTarget(null);
    },
    [setNodes],
  );

  /* ---------- 主体节点（P2-主体） ---------- */
  const patchSubject = useCallback(
    (id: string, patch: Partial<SubjectPayload>) => {
      const n = useCanvasStore.getState().doc.nodes.find((x) => x.id === id);
      if (n === undefined || !isSubjectPayload(n.payload)) {
        return;
      }
      const nextPayload: SubjectPayload = { ...n.payload, ...patch };
      useCanvasStore.getState().updateNode(id, { payload: nextPayload, title: nextPayload.name });
      refreshNodeData(id);
    },
    [refreshNodeData],
  );

  /** 主体“生成prompt”：用名称+描述拼提示词（不依赖上游），并补默认负向 */
  const composeSubjectPrompt = useCallback(
    (node: CanvasNode) => {
      if (!isSubjectPayload(node.payload) || node.params === undefined) {
        return;
      }
      const prompt = subjectPrompt(node.payload);
      const base = node.params.negativePrompt !== undefined && node.params.negativePrompt !== '' ? node.params.negativePrompt : defaultNegativePrompt();
      const negative = node.payload.category === 'scene' ? `${base}，${sceneNegative('empty-scene')}` : base;
      useCanvasStore.getState().updateNode(node.id, { params: { ...node.params, prompt, negativePrompt: negative } });
      refreshNodeData(node.id);
      setStatus('已生成提示词');
    },
    [refreshNodeData],
  );

  const generateSubjectTurnaround = useCallback(
    async (node: CanvasNode) => {
      if (!isSubjectPayload(node.payload)) {
        return;
      }
      setGeneratingId(node.id);
      setStatus('生成视图…');
      try {
        const imgs = await generationProvider.generateTurnarounds({ subject: node.payload, aspectRatio: '3:4', resolution: '2K' });
        const labels = turnaroundPrompts(node.payload).map((t) => t.label);
        const stamp = Date.now().toString(36);
        const viewNodes: CanvasNode[] = imgs.map((img, i) => ({
          id: `n-${stamp}-v${i}`,
          kind: 'image',
          mode: 'derived',
          title: `${labels[i] ?? '视图'}·${node.title}`,
          position: { x: node.position.x + 260, y: node.position.y + i * 210 },
          media: { mediaId: `media-${stamp}-${i}`, url: img.url, thumbnail: img.thumbnail, width: img.width, height: img.height },
        }));
        // 一次事务派生 3 个独立视图节点并各连线（下游可连具体某视图）
        useCanvasStore.getState().spawnViews(node.id, viewNodes);
        // 将本批视图挂到当前造型（非破坏追加）
        const cur = useCanvasStore.getState().doc.nodes.find((x) => x.id === node.id);
        if (cur !== undefined && isSubjectPayload(cur.payload)) {
          const withLooks = ensureLooks(cur.payload);
          useCanvasStore
            .getState()
            .updateNode(node.id, { payload: attachViewsToLook(withLooks, withLooks.activeLookId, viewNodes.map((v) => v.id)) });
        }
        syncFromDoc();
        setStatus(`已生成 ${viewNodes.length} 个视图节点`);
      } catch {
        setStatus('生成失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [syncFromDoc],
  );

  /** 从剧本一键生成资产卡：抽取角色/场景→建主体节点预填名称/描述，一次事务连线到源 */
  const spawnSubjectCards = useCallback(
    (sourceNode: CanvasNode) => {
      if (!isTextGenPayload(sourceNode.payload)) {
        return;
      }
      const seeds = extractEntities(sourceNode.payload.result);
      if (seeds.length === 0) {
        setStatus('上游剧本暂无可抽取的角色/场景（请先生成导演剧本）');
        return;
      }
      const stamp = Date.now().toString(36);
      const nodes: CanvasNode[] = seeds.map((seed, i) => {
        const id = `n-${stamp}-s${i}`;
        const base = createSubjectNode(seed.category, id, { x: sourceNode.position.x + 340, y: sourceNode.position.y + i * 170 });
        if (!isSubjectPayload(base.payload)) {
          return base;
        }
        // 角色多造型（方案①）：剧本有造型描写才预填多 look，否则保留默认造型
        const looks =
          seed.category === 'character' && seed.looks.length > 0
            ? seed.looks.map((nm, li) => ({ id: `look-${id}-${li}`, name: nm, viewNodeIds: [] }))
            : base.payload.looks;
        const activeLookId = looks[0]?.id ?? base.payload.activeLookId;
        return {
          ...base,
          title: seed.name,
          payload: { ...base.payload, name: seed.name, description: seed.description, looks, activeLookId },
        };
      });
      useCanvasStore.getState().spawnViews(sourceNode.id, nodes);
      syncFromDoc();
      setStatus(`已生成 ${nodes.length} 张资产卡`);
    },
    [syncFromDoc],
  );

  /** 从文学剧本拆分成导演剧本：自动派生一张导演剧本节点 + 连线，并依上游结果即时生成 */
  const splitToStoryboard = useCallback(
    async (sourceNode: CanvasNode) => {
      if (
        !isTextGenPayload(sourceNode.payload) ||
        !isLiteraryScriptResult(sourceNode.payload.result)
      ) {
        setStatus('请先生成文学剧本，再拆分成导演剧本');
        return;
      }
      const s = useCanvasStore.getState();
      const id = nextNodeId(s.doc.nodes.map((n) => n.id));
      const pos = { x: sourceNode.position.x + 300, y: sourceNode.position.y };
      const node = createTextGenNode('scriptToStoryboard', id, pos);
      if (!s.addNode(node)) {
        return;
      }
      s.connect(sourceNode.id, id); // 文学剧本 → 导演剧本（runGenerate 依此边读上游）
      syncFromDoc();
      if (isTextGenPayload(node.payload)) {
        await runGenerate(id, node.payload);
      }
      setStatus('已拆分成导演剧本');
    },
    [syncFromDoc, runGenerate],
  );

  /** 展开分镜：把导演剧本总表物化成 N 个独立分镜节点（一次事务=一步撤销），各连线到源 */
  const explodeStoryboard = useCallback(
    (sourceNode: CanvasNode) => {
      if (
        !isTextGenPayload(sourceNode.payload) ||
        !isStoryboardResult(sourceNode.payload.result)
      ) {
        setStatus('请先生成导演剧本，再展开分镜');
        return;
      }
      const shots = sourceNode.payload.result.storyboard.shots;
      if (shots.length === 0) {
        setStatus('导演剧本暂无分镜');
        return;
      }
      const s = useCanvasStore.getState();
      const stamp = Date.now().toString(36);
      const shotNodes = shots.map((sh, i) =>
        createShotNode(
          sh,
          `n-${stamp}-sh${i}`,
          { x: sourceNode.position.x + 320, y: sourceNode.position.y + i * 150 },
          1,
          sourceNode.id,
        ),
      );
      s.spawnViews(sourceNode.id, shotNodes);
      syncFromDoc();
      setStatus(`已展开 ${shotNodes.length} 个分镜节点`);
    },
    [syncFromDoc],
  );

  /** 改分镜节点当前镜字段：写回 payload + 同步标题/卡面（不清选中） */
  const patchShotNode = useCallback(
    (id: string, payload: ShotPayload, patch: Partial<Omit<StoryboardShot, 'id' | 'index'>>) => {
      const next = patchShot(payload, patch);
      useCanvasStore
        .getState()
        .updateNode(id, { payload: next, title: shotLabel(next.episodeIndex, next.shot.index) });
      refreshNodeData(id);
    },
    [refreshNodeData],
  );

  /** 分镜节点→生成关键帧（连线的图生成节点，prompt=本镜内容） */
  const spawnKeyframeFromShot = useCallback(
    (shotNode: CanvasNode) => {
      if (!isShotPayload(shotNode.payload)) {
        return;
      }
      const s = useCanvasStore.getState();
      const id = nextNodeId(s.doc.nodes.map((n) => n.id));
      const base = createImageGenNode(id, { x: shotNode.position.x + 280, y: shotNode.position.y });
      const kf: CanvasNode =
        base.params !== undefined
          ? { ...base, params: { ...base.params, prompt: composeImagePromptFromShot(shotNode.payload.shot), negativePrompt: defaultNegativePrompt() } }
          : base;
      s.spawnViews(shotNode.id, [kf]);
      syncFromDoc();
      setStatus('已生成关键帧节点');
    },
    [syncFromDoc],
  );

  /** 分镜节点→加配音（音频节点，逐句拆台词：多人对话每行一角色） */
  const spawnAudioFromShot = useCallback(
    (shotNode: CanvasNode) => {
      if (!isShotPayload(shotNode.payload)) {
        return;
      }
      const lines = parseDialogueLines(shotNode.payload.shot);
      if (lines.length === 0) {
        setStatus('本镜无台词，无需配音');
        return;
      }
      const s = useCanvasStore.getState();
      const id = nextNodeId(s.doc.nodes.map((n) => n.id));
      const node = createAudioNode(id, { x: shotNode.position.x + 280, y: shotNode.position.y + 160 }, lines);
      s.spawnViews(shotNode.id, [node]);
      syncFromDoc();
      setStatus(`已添加配音节点（${lines.length} 句）`);
    },
    [syncFromDoc],
  );

  /** 分镜节点→生成分镜视频（视频生成节点，prompt=8 层拼装） */
  const spawnVideoFromShot = useCallback(
    (shotNode: CanvasNode) => {
      if (!isShotPayload(shotNode.payload)) {
        return;
      }
      const shot = shotNode.payload.shot;
      const s = useCanvasStore.getState();
      const id = nextNodeId(s.doc.nodes.map((n) => n.id));
      const base = createVideoGenNode(id, { x: shotNode.position.x + 280, y: shotNode.position.y + 320 }, shotLabel(shotNode.payload.episodeIndex, shot.index) + ' 视频');
      const node: CanvasNode =
        base.params !== undefined
          ? { ...base, params: { ...base.params, prompt: composeVideoPrompt(shot), durationSec: shot.durationSec } }
          : base;
      s.spawnViews(shotNode.id, [node]);
      syncFromDoc();
      setStatus('已生成分镜视频节点');
    },
    [syncFromDoc],
  );

  /** 生成视频（mock）：回写 node.media（首帧缩略+时长） */
  const runGenerateVideo = useCallback(
    async (node: CanvasNode) => {
      if (node.params === undefined) {
        return;
      }
      setGeneratingId(node.id);
      setStatus('视频生成中…');
      try {
        const v = await generationProvider.generateVideo({
          prompt: node.params.prompt,
          aspectRatio: node.params.aspectRatio,
          resolution: node.params.resolution,
          durationSec: node.params.durationSec,
        });
        const media: MediaRef = { mediaId: `media-${node.id}`, url: v.url, thumbnail: v.thumbnail, durationSec: v.durationSec };
        useCanvasStore.getState().updateNode(node.id, { media });
        refreshNodeData(node.id);
        setStatus('已生成分镜视频');
      } catch {
        setStatus('视频生成失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [refreshNodeData],
  );

  /** 视频工具箱（P-B4）：选帧/剪辑/高清/去字幕/翻译/音画分离 → 派生节点 */
  const runVideoTool = useCallback(
    async (node: CanvasNode, op: VideoToolOp) => {
      if (node.media === undefined) {
        return;
      }
      setGeneratingId(node.id);
      setStatus('视频处理…');
      try {
        const outs = await generationProvider.editVideo(op, { videoUrl: node.media.url });
        const s = useCanvasStore.getState();
        const stamp = Date.now().toString(36);
        const derived: CanvasNode[] = outs.map((o, i) => ({
          id: `n-${stamp}-vt${i}`,
          kind: o.kind,
          mode: 'derived',
          title: `${o.label}·${node.title}`,
          position: { x: node.position.x + 280, y: node.position.y + i * 150 },
          media: { mediaId: `media-${stamp}-${i}`, url: o.url, thumbnail: o.thumbnail },
        }));
        s.spawnViews(node.id, derived);
        syncFromDoc();
        const first = derived[0];
        if (first !== undefined) {
          rfRef.current?.fitView({ nodes: [{ id: first.id }], duration: 500, padding: 0.5 });
        }
        setStatus(`已完成：${outs.map((o) => o.label).join('、')}`);
      } catch {
        setStatus('视频处理失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [syncFromDoc],
  );

  /** 生成配音（mock TTS）：逐句合成→回写该行 url */
  const runGenerateAudio = useCallback(
    async (node: CanvasNode) => {
      if (!isAudioPayload(node.payload) || node.payload.lines.length === 0) {
        return;
      }
      setGeneratingId(node.id);
      setStatus('配音合成中…');
      try {
        let payload = node.payload;
        for (let i = 0; i < payload.lines.length; i += 1) {
          const line = payload.lines[i];
          if (line === undefined) {
            continue;
          }
          const roleV = roleVoiceByName(line.role);
          const a = await generationProvider.tts({
            text: line.text,
            voiceId: line.voiceId ?? roleV?.voiceId ?? defaultVoice().voiceId,
            emotion: line.emotion,
          });
          payload = updateAudioLine(payload, i, { url: a.url });
        }
        useCanvasStore.getState().updateNode(node.id, { payload });
        refreshNodeData(node.id);
        setStatus('已生成全部配音');
      } catch {
        setStatus('配音失败');
      } finally {
        setGeneratingId(null);
      }
    },
    [refreshNodeData],
  );

  /** 改视频 prompt */
  const patchVideoPrompt = useCallback(
    (id: string, prompt: string) => {
      const n = useCanvasStore.getState().doc.nodes.find((x) => x.id === id);
      if (n === undefined || n.params === undefined) {
        return;
      }
      useCanvasStore.getState().updateNode(id, { params: { ...n.params, prompt } });
      refreshNodeData(id);
    },
    [refreshNodeData],
  );

  /** 逐句改配音行（角色/台词/情绪/音色） */
  const patchAudioLine = useCallback(
    (id: string, payload: AudioPayload, index: number, patch: Partial<AudioLine>) => {
      useCanvasStore.getState().updateNode(id, { payload: updateAudioLine(payload, index, patch) });
      refreshNodeData(id);
    },
    [refreshNodeData],
  );

  /** 从上游分镜/文本组装图像提示词（§11.4 镜头语义注入） */
  const runComposeFromUpstream = useCallback(
    (node: CanvasNode) => {
      if (node.params === undefined) {
        return;
      }
      const s = useCanvasStore.getState();
      const fromId = s.doc.edges.find((e) => e.to === node.id)?.from;
      const up = fromId !== undefined ? s.doc.nodes.find((n) => n.id === fromId) : undefined;
      const result = up !== undefined && isTextGenPayload(up.payload) ? up.payload.result : undefined;
      const composed = composeImagePrompt(result, node.params.prompt);
      const negative = node.params.negativePrompt !== undefined && node.params.negativePrompt !== '' ? node.params.negativePrompt : defaultNegativePrompt();
      s.updateNode(node.id, { params: { ...node.params, prompt: composed, negativePrompt: negative } });
      refreshNodeData(node.id);
      setStatus('已从上游生成提示词');
    },
    [refreshNodeData],
  );

  const uploadImage = useCallback(
    (node: CanvasNode, file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const url = typeof reader.result === 'string' ? reader.result : '';
        if (url === '') {
          setStatus('上传失败');
          return;
        }
        const media: MediaRef = { mediaId: `media-${Date.now().toString(36)}`, url, thumbnail: url };
        useCanvasStore.getState().updateNode(node.id, { media });
        refreshNodeData(node.id);
        setStatus('已上传图片');
      };
      reader.onerror = () => setStatus('上传失败');
      reader.readAsDataURL(file);
    },
    [refreshNodeData],
  );

  /** 在 3D 导演台打开：记下目标关键帧节点，跳转导演台，由导演台落成关键帧回写 */
  const openInDirector = useCallback(
    (id: string) => {
      useCanvasStore.getState().setDirectorTarget(id);
      navigate('/director');
    },
    [navigate],
  );

  // Ctrl/Cmd+Z 撤销、Ctrl/Cmd+Y 或 Ctrl/Cmd+Shift+Z 重做；焦点在输入框时不拦截（D-49 反馈 3）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
        return;
      }
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === 'z' && !e.shiftKey) {
        e.preventDefault();
        doUndo();
      } else if ((e.ctrlKey || e.metaKey) && (k === 'y' || (k === 'z' && e.shiftKey))) {
        e.preventDefault();
        doRedo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doUndo, doRedo]);

  // 下拉展开时：点外部或 Esc 收起（D-49 反馈 1）
  useEffect(() => {
    if (!addOpen) {
      return;
    }
    const onDown = (e: MouseEvent) => {
      if (addRef.current !== null && !addRef.current.contains(e.target as Element)) {
        setAddOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAddOpen(false);
      }
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [addOpen]);

  // 进入重命名时自动聚焦并选中全文
  useEffect(() => {
    if (editingTitle) {
      setDraftTitle(doc.title);
      titleInputRef.current?.select();
    }
  }, [editingTitle, doc.title]);

  const commitTitle = useCallback(() => {
    const t = draftTitle.trim();
    if (t !== '') {
      setTitle(t);
    }
    setEditingTitle(false);
  }, [draftTitle, setTitle]);

  /* ---------- 右键上下文菜单（C-3b） ---------- */
  const closeMenu = useCallback(() => {
    setMenu(null);
    setRenaming(false);
  }, []);

  const openMenu = useCallback((m: MenuState) => {
    rawMenuPos.current = { x: m.x, y: m.y }; // 原始点击点（菜单可能靠边被夹位，添加节点落点用原始值）
    const estH = m.kind === 'pane' ? 300 : 140;
    const x = Math.max(8, Math.min(m.x, window.innerWidth - 168));
    const y = Math.max(8, Math.min(m.y, window.innerHeight - estH - 8));
    setRenaming(false);
    setMenu({ ...m, x, y });
  }, []);

  const onNodeContextMenu = useCallback(
    (e: React.MouseEvent, node: Node) => {
      e.preventDefault();
      openMenu({ kind: 'node', x: e.clientX, y: e.clientY, id: node.id });
    },
    [openMenu],
  );
  const onEdgeContextMenu = useCallback(
    (e: React.MouseEvent, edge: Edge) => {
      e.preventDefault();
      openMenu({ kind: 'edge', x: e.clientX, y: e.clientY, id: edge.id });
    },
    [openMenu],
  );
  const onPaneContextMenu = useCallback(
    (e: React.MouseEvent | MouseEvent) => {
      e.preventDefault();
      openMenu({ kind: 'pane', x: e.clientX, y: e.clientY });
    },
    [openMenu],
  );

  /** 双击空白→在光标处弹“添加节点”菜单（对齐竞品“双击新建”） */
  const onCanvasDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest('.react-flow__pane')) {
        return;
      }
      openMenu({ kind: 'pane', x: e.clientX, y: e.clientY });
    },
    [openMenu],
  );

  const onCanvasDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
    }
  }, []);

  /** 拖本地图片/视频/音频入画布→自动建对应 source 节点 */
  const onCanvasDrop = useCallback(
    async (e: React.DragEvent) => {
      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) {
        return;
      }
      e.preventDefault();
      const flow = rfRef.current?.screenToFlowPosition({ x: e.clientX, y: e.clientY }) ?? { x: e.clientX, y: e.clientY };
      const created: CanvasNode[] = [];
      for (const f of files) {
        const kind = f.type.startsWith('image/')
          ? 'image'
          : f.type.startsWith('video/')
            ? 'video'
            : f.type.startsWith('audio/')
              ? 'audio'
              : null;
        if (kind === null) {
          continue;
        }
        try {
          const url = await readFileAsDataUrl(f);
          const id = nextNodeId([...useCanvasStore.getState().doc.nodes.map((n) => n.id), ...created.map((c) => c.id)]);
          created.push({
            id,
            kind,
            mode: 'source',
            title: f.name.replace(/\.[^.]+$/, '').slice(0, 20) || kind,
            position: { x: flow.x + created.length * 28, y: flow.y + created.length * 28 },
            media: { mediaId: `media-${id}`, url },
          });
        } catch {
          /* 单文件读失败跳过 */
        }
      }
      if (created.length === 0) {
        setStatus('没有可导入的图片/视频/音频文件');
        return;
      }
      const s = useCanvasStore.getState();
      for (const n of created) {
        s.addNode(n);
      }
      syncFromDoc();
      setStatus(`已导入 ${created.length} 个素材节点`);
    },
    [syncFromDoc],
  );

  /** 组合：把选中的≥2 个非组节点框成一个主题色组合框（轻量，不改成员） */
  const groupSelected = useCallback(() => {
    const sel = nodes.filter((n) => n.selected && n.type !== 'groupCard');
    if (sel.length < 2) {
      setStatus('至少选中 2 个节点再组合');
      return;
    }
    const minX = Math.min(...sel.map((n) => n.position.x)) - 24;
    const minY = Math.min(...sel.map((n) => n.position.y)) - 40;
    const maxX = Math.max(...sel.map((n) => n.position.x)) + 260;
    const maxY = Math.max(...sel.map((n) => n.position.y)) + 220;
    const s = useCanvasStore.getState();
    const id = nextNodeId(s.doc.nodes.map((n) => n.id));
    const payload: GroupPayload = {
      type: 'group',
      label: '组合',
      color: GROUP_COLORS[0] ?? 'amber',
      memberIds: sel.map((n) => n.id),
      width: maxX - minX,
      height: maxY - minY,
    };
    s.addNode(createGroupNode(id, { x: minX, y: minY }, payload));
    syncFromDoc();
    setStatus(`已组合 ${sel.length} 个节点`);
  }, [nodes, syncFromDoc]);

  /** 解组：删除选中的组合框（成员保留） */
  const ungroupSelected = useCallback(() => {
    const ids = nodes.filter((n) => n.selected && n.type === 'groupCard').map((n) => n.id);
    if (ids.length === 0) {
      return;
    }
    const s = useCanvasStore.getState();
    for (const id of ids) {
      s.removeNode(id);
    }
    syncFromDoc();
    setStatus('已解组');
  }, [nodes, syncFromDoc]);

  /** 换主题色 */
  const setGroupColor = useCallback(
    (id: string, color: string) => {
      const n = useCanvasStore.getState().doc.nodes.find((x) => x.id === id);
      if (n === undefined || !isGroupPayload(n.payload)) {
        return;
      }
      useCanvasStore.getState().updateNode(id, { payload: { ...n.payload, color } });
      refreshNodeData(id);
    },
    [refreshNodeData],
  );

  /** 批量下载选中节点的媒体 */
  const downloadSelected = useCallback(() => {
    const doc = useCanvasStore.getState().doc;
    const sel = nodes.filter((n) => n.selected && n.type !== 'groupCard');
    let count = 0;
    for (const s of sel) {
      const node = doc.nodes.find((x) => x.id === s.id);
      if (node?.media !== undefined) {
        downloadDataUrl(node.media.url, node.title || node.id);
        count += 1;
      }
    }
    setStatus(count > 0 ? `已下载 ${count} 个素材` : '选中项没有可下载的媒体');
  }, [nodes]);

  /** 选中子图（节点 + 两端都选中的边） */
  const selectedSubgraph = useCallback(() => {
    const doc = useCanvasStore.getState().doc;
    const ids = new Set(nodes.filter((n) => n.selected && n.type !== 'groupCard').map((n) => n.id));
    return {
      selNodes: doc.nodes.filter((n) => ids.has(n.id)),
      selEdges: doc.edges.filter((e) => ids.has(e.from) && ids.has(e.to)),
    };
  }, [nodes]);

  /** 存为模板（自动命名） */
  const saveSelectionAsTemplate = useCallback(() => {
    const { selNodes, selEdges } = selectedSubgraph();
    if (selNodes.length === 0) {
      setStatus('先选中要存为模板的节点');
      return;
    }
    const meta = saveTemplate(selNodes, selEdges, `模板 ${listTemplates().length + 1}`);
    if (meta !== null) {
      setTemplateCount(listTemplates().length);
    }
    setStatus(meta === null ? '模板保存失败（存储不可用）' : `已存为模板「${meta.name}」`);
  }, [selectedSubgraph]);

  /** 导入模板到当前画布（换全新 id + 保留连接 + 偏移） */
  const importTemplateById = useCallback(
    (id: string) => {
      const tpl = loadTemplate(id);
      if (tpl === null) {
        setStatus('模板载入失败');
        return;
      }
      const existing = useCanvasStore.getState().doc.nodes.map((n) => n.id);
      const made: string[] = [];
      const { nodes: nn, edges: ne } = remapTemplate(
        tpl,
        () => {
          const nid = nextNodeId([...existing, ...made]);
          made.push(nid);
          return nid;
        },
        { x: 80, y: 80 },
      );
      const st = useCanvasStore.getState();
      for (const n of nn) {
        st.addNode(n);
      }
      for (const e of ne) {
        st.connect(e.from, e.to);
      }
      syncFromDoc();
      setTemplatesOpen(false);
      setStatus(`已导入模板（${nn.length} 个节点）`);
    },
    [syncFromDoc],
  );

  const openTemplates = useCallback(() => {
    setTemplates(listTemplates());
    setTemplatesOpen(true);
  }, []);
  const deleteTemplateById = useCallback((id: string) => {
    deleteTemplate(id);
    setTemplates(listTemplates());
    setTemplateCount(listTemplates().length);
  }, []);

  /** 多宫格拼接：选中≥2 个带媒体的节点（图片/主体都可）→canvas 合成一张→新图节点 */
  const stitchGrid = useCallback(async () => {
    const doc = useCanvasStore.getState().doc;
    const imgs = nodes
      .filter((n) => n.selected && n.type !== 'groupCard')
      .map((n) => doc.nodes.find((x) => x.id === n.id))
      .filter((x): x is CanvasNode => x !== undefined && x.media !== undefined);
    if (imgs.length < 2) {
      setStatus('至少选 2 个带图的节点（图片/主体）再拼多宫格');
      return;
    }
    const cell = 512;
    const { cols, rows } = gridLayout(imgs.length);
    const canvas = document.createElement('canvas');
    canvas.width = cols * cell;
    canvas.height = rows * cell;
    const ctx = canvas.getContext('2d');
    if (ctx === null) {
      return;
    }
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const loaded = await Promise.all(
      imgs.map(
        (im) =>
          new Promise<HTMLImageElement | null>((res) => {
            const el = new Image();
            el.onload = () => res(el);
            el.onerror = () => res(null);
            el.src = im.media?.url ?? '';
          }),
      ),
    );
    loaded.forEach((el, i) => {
      if (el !== null) {
        ctx.drawImage(el, (i % cols) * cell, Math.floor(i / cols) * cell, cell, cell);
      }
    });
    const url = canvas.toDataURL('image/png');
    const st = useCanvasStore.getState();
    const id = nextNodeId(st.doc.nodes.map((n) => n.id));
    st.addNode({ id, kind: 'image', mode: 'source', title: `多宫格 ${imgs.length}`, position: { x: 80, y: 80 }, media: { mediaId: `media-${id}`, url } });
    syncFromDoc();
    setStatus(`已拼接 ${imgs.length} 张为多宫格`);
  }, [nodes, syncFromDoc]);

  const startRename = useCallback((id: string) => {
    const t = useCanvasStore.getState().doc.nodes.find((n) => n.id === id)?.title ?? '';
    setRenameDraft(t);
    setRenaming(true);
  }, []);

  const commitRename = useCallback(() => {
    if (menu === null || menu.kind !== 'node') {
      return;
    }
    const t = renameDraft.trim();
    if (t !== '') {
      useCanvasStore.getState().updateNode(menu.id, { title: t });
      setNodes((ns) => ns.map((n) => (n.id === menu.id ? { ...n, data: { ...n.data, title: t } } : n)));
    }
    closeMenu();
  }, [menu, renameDraft, setNodes, closeMenu]);

  const doDuplicate = useCallback(() => {
    if (menu === null || menu.kind !== 'node') {
      return;
    }
    useCanvasStore.getState().duplicateNode(menu.id);
    syncFromDoc();
    setStatus('已复制节点');
    closeMenu();
  }, [menu, syncFromDoc, closeMenu]);

  const doDeleteNode = useCallback(() => {
    if (menu === null || menu.kind !== 'node') {
      return;
    }
    useCanvasStore.getState().removeNode(menu.id);
    syncFromDoc();
    setStatus('已删除节点');
    closeMenu();
  }, [menu, syncFromDoc, closeMenu]);

  const doDeleteEdge = useCallback(() => {
    if (menu === null || menu.kind !== 'edge') {
      return;
    }
    useCanvasStore.getState().removeEdge(menu.id);
    syncFromDoc();
    setStatus('已删除连线');
    closeMenu();
  }, [menu, syncFromDoc, closeMenu]);

  const addAtPane = useCallback(
    (kind: NodeKind) => {
      const flow = rfRef.current?.screenToFlowPosition(rawMenuPos.current);
      handleAdd(kind, flow);
    },
    [handleAdd],
  );

  const addTextAtPane = useCallback(
    (capability: TextGenCapability) => {
      const flow = rfRef.current?.screenToFlowPosition(rawMenuPos.current);
      addTextNode(capability, flow);
    },
    [addTextNode],
  );

  const addSubjectAtPane = useCallback(
    (category: SubjectCategory) => {
      const flow = rfRef.current?.screenToFlowPosition(rawMenuPos.current);
      addSubjectNode(category, flow);
    },
    [addSubjectNode],
  );

  // 菜单开着时：点菜单外或 Esc 收起；平移/缩放时也收起（避免悬空错位）
  useEffect(() => {
    if (menu === null) {
      return;
    }
    const onDown = (e: MouseEvent) => {
      if (menuRef.current !== null && !menuRef.current.contains(e.target as Element)) {
        closeMenu();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenu();
      }
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu, closeMenu]);

  // 选中的单个文本生成节点→弹生成面板（从 store doc 读 payload，受控于 doc）
  const selectedId = nodes.find((n) => n.selected)?.id;
  const selNode = selectedId !== undefined ? doc.nodes.find((n) => n.id === selectedId) : undefined;
  const genPayload = selNode !== undefined && isTextGenPayload(selNode.payload) ? selNode.payload : undefined;
  const selImageNode =
    selNode !== undefined && selNode.kind === 'image' && selNode.mode === 'generator' ? selNode : undefined;
  const selSubjectPayload =
    selNode !== undefined && isSubjectPayload(selNode.payload) ? selNode.payload : undefined;
  const selShotPayload =
    selNode !== undefined && isShotPayload(selNode.payload) ? selNode.payload : undefined;
  const selVideoNode =
    selNode !== undefined && selNode.kind === 'video' && selNode.mode === 'generator' ? selNode : undefined;
  const selAudioNode =
    selNode !== undefined && selNode.kind === 'audio' && isAudioPayload(selNode.payload) ? selNode : undefined;
  const selAudioPayload = selAudioNode !== undefined && isAudioPayload(selAudioNode.payload) ? selAudioNode.payload : undefined;

  // 多选→组合/下载工具条用
  const selectedRealNodes = nodes.filter((n) => n.selected && n.type !== 'groupCard');
  const selectedGroupNodes = nodes.filter((n) => n.selected && n.type === 'groupCard');
  const selectedTotal = selectedRealNodes.length + selectedGroupNodes.length;
  const soleGroupId = selectedGroupNodes.length === 1 ? selectedGroupNodes[0]?.id : undefined;

  // 面板初始定位到节点右侧（读节点 DOM 矩形）；选中节点变→重算 anchor
  useEffect(() => {
    if (
      selectedId === undefined ||
      (genPayload === undefined &&
        selImageNode === undefined &&
        selSubjectPayload === undefined &&
        selShotPayload === undefined &&
        selVideoNode === undefined &&
        selAudioNode === undefined)
    ) {
      setAnchor(null);
      return;
    }
    const el = document.querySelector(`.react-flow__node[data-id="${selectedId}"]`);
    if (el === null) {
      setAnchor(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setAnchor({
      x: Math.max(8, Math.min(r.right + 12, window.innerWidth - 340)),
      y: Math.max(8, Math.min(r.top, window.innerHeight - 220)),
    });
  }, [selectedId, genPayload, selImageNode, selSubjectPayload, selShotPayload, selVideoNode, selAudioNode]);

  return (
    <div className="flex h-full flex-col">
      <header className="relative flex items-center gap-3 border-b border-surface-line bg-surface-overlay px-4 py-2">
        <button
          type="button"
          className="btn-ghost inline-flex items-center gap-1.5"
          onClick={() => navigate('/canvas')}
        >
          <ArrowLeft size={15} />
          返回
        </button>
        <button type="button" className="btn-ghost inline-flex items-center gap-1.5" onClick={openTemplates}>
          <Library size={15} />
          模板库{templateCount > 0 && <span className="rounded-full bg-accent/15 px-1.5 text-uiSm font-num text-accent">{templateCount}</span>}
        </button>
        <div ref={addRef} className="relative">
          <button
            type="button"
            className="btn-ghost inline-flex items-center gap-1.5"
            aria-haspopup="menu"
            aria-expanded={addOpen}
            onClick={() => setAddOpen((o) => !o)}
          >
            <Plus size={15} />
            添加节点
          </button>
          {addOpen && (
            <div className="absolute left-0 top-full z-20 mt-1 flex w-36 flex-col gap-0.5 rounded-card border border-surface-edge bg-surface-overlay p-1 shadow-[0_6px_20px_rgba(0,0,0,.5)]">
              <div className="px-2 pb-0.5 pt-1 text-uiSm text-ink-faint">文本生成</div>
              <button
                type="button"
                role="menuitem"
                className="rounded-control px-2 py-1 text-left text-ui text-ink-secondary transition-colors hover:bg-surface-raised hover:text-ink-primary"
                onClick={() => addTextNode('textToScript')}
              >
                文学剧本
              </button>
              <div className="my-1 h-px bg-surface-line" />
              <div className="px-2 pb-0.5 text-uiSm text-ink-faint">资产</div>
              <button type="button" role="menuitem" className="rounded-control px-2 py-1 text-left text-ui text-ink-secondary transition-colors hover:bg-surface-raised hover:text-ink-primary" onClick={() => addSubjectNode('character')}>
                角色
              </button>
              <button type="button" role="menuitem" className="rounded-control px-2 py-1 text-left text-ui text-ink-secondary transition-colors hover:bg-surface-raised hover:text-ink-primary" onClick={() => addSubjectNode('scene')}>
                场景
              </button>
              <button type="button" role="menuitem" className="rounded-control px-2 py-1 text-left text-ui text-ink-secondary transition-colors hover:bg-surface-raised hover:text-ink-primary" onClick={() => addSubjectNode('prop')}>
                道具
              </button>
              <div className="my-1 h-px bg-surface-line" />
              <div className="px-2 pb-0.5 text-uiSm text-ink-faint">添加节点</div>
              {(Object.keys(NODE_KIND_LABELS) as NodeKind[]).filter((k) => k !== 'subject' && k !== 'shot' && k !== 'group').map((k) => (
                <button
                  key={k}
                  type="button"
                  role="menuitem"
                  className="rounded-control px-2 py-1 text-left text-ui text-ink-secondary transition-colors hover:bg-surface-raised hover:text-ink-primary"
                  onClick={() => handleAdd(k)}
                >
                  {NODE_KIND_LABELS[k]}
                </button>
              ))}
            </div>
          )}
        </div>
        {editingTitle ? (
          <input
            ref={titleInputRef}
            value={draftTitle}
            maxLength={60}
            className="w-48 rounded-control border border-surface-edge bg-surface-base px-2 py-1 text-ui font-semibold text-ink-primary outline-none focus:border-accent"
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitTitle();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setEditingTitle(false);
              }
            }}
          />
        ) : (
          <h1
            className="cursor-text truncate text-ui font-semibold"
            title="点击修改画布名"
            onClick={() => setEditingTitle(true)}
          >
            {doc.title}
          </h1>
        )}
        {/* 计数：顶栏正中、粗体、节点/连线各用一色（用户 2026-09-30：靠右易忽略） */}
        <div className="pointer-events-none absolute left-1/2 flex -translate-x-1/2 items-center gap-1 text-ui">
          <span className="font-num font-bold text-accent">{doc.nodes.length}</span>
          <span className="text-ink-muted">节点</span>
          <span className="mx-1 text-ink-faint">·</span>
          <span className="font-num font-bold text-state-info">{doc.edges.length}</span>
          <span className="text-ink-muted">连线</span>
        </div>
        {/* 状态：靠右显示（撤销/重做已下沉到左侧控件组） */}
        {status !== null && (
          <span className="ml-auto shrink-0 text-uiSm text-ink-muted">{status}</span>
        )}
      </header>
      <div className="relative min-h-0 flex-1" onDoubleClick={onCanvasDoubleClick} onDragOver={onCanvasDragOver} onDrop={(e) => void onCanvasDrop(e)}>
        {selectedTotal > 0 && (
          <div className="pointer-events-auto absolute left-1/2 top-2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-card border border-surface-edge bg-surface-overlay px-2.5 py-1.5 text-uiSm shadow-[0_6px_20px_rgba(0,0,0,.5)]">
            <span className="text-ink-faint">已选 {selectedTotal}</span>
            {selectedRealNodes.length >= 2 && (
              <button type="button" onClick={groupSelected} className="rounded-control bg-accent/15 px-2 py-0.5 font-semibold text-accent hover:bg-accent/25">
                组合
              </button>
            )}
            {selectedRealNodes.length >= 1 && (
              <button type="button" onClick={downloadSelected} className="text-ink-secondary hover:text-ink-primary">
                下载选中
              </button>
            )}
            {selectedRealNodes.length >= 2 && (
              <button type="button" onClick={() => void stitchGrid()} className="text-ink-secondary hover:text-ink-primary">
                多宫格
              </button>
            )}
            {selectedRealNodes.length >= 1 && (
              <button type="button" onClick={saveSelectionAsTemplate} className="text-ink-secondary hover:text-ink-primary">
                存为模板
              </button>
            )}
            {selectedGroupNodes.length >= 1 && (
              <button type="button" onClick={ungroupSelected} className="text-ink-secondary hover:text-ink-primary">
                解组
              </button>
            )}
            {soleGroupId !== undefined &&
              GROUP_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label="设为主题色"
                  onClick={() => setGroupColor(soleGroupId, c)}
                  className={`group-swatch gc-${c} h-4 w-4 rounded-full border border-surface-edge`}
                />
              ))}
          </div>
        )}
        {genPayload !== undefined && selNode !== undefined && anchor !== null && (
          <FloatingPanel
            key={selNode.id}
            title={genPayload.capability === 'textToScript' ? '文学剧本创作' : '导演剧本'}
            x={anchor.x}
            y={anchor.y}
            onClose={() => setNodes((ns) => ns.map((n) => ({ ...n, selected: false })))}
          >
            <div className="flex flex-col gap-2">
              <textarea
                value={genPayload.prompt}
                placeholder={
                  genPayload.capability === 'textToScript'
                    ? '写下故事梗概 / 题材 / 人物关系…'
                    : '补充拆分要求（集数、节奏、风格…）；留空则依上游文学剧本自动拆分…'
                }
                className="h-28 w-full shrink-0 resize-none bg-transparent px-1 py-1 text-ui text-ink-primary outline-none"
                onChange={(e) => setPrompt(selNode.id, genPayload, e.target.value)}
              />
              {genPayload.capability === 'textToScript' && (
                <div className="flex flex-wrap items-center gap-1.5 border-t border-surface-line pt-2">
                  <span className="text-uiSm text-ink-faint">技能</span>
                  {SCRIPT_SKILLS.map((sk) => (
                    <button
                      key={sk.id}
                      type="button"
                      title={sk.text}
                      onClick={() => setPrompt(selNode.id, genPayload, applySkillSnippet(genPayload.prompt, sk))}
                      className="rounded-control border border-surface-line bg-surface-base px-2 py-0.5 text-uiSm text-ink-secondary transition-colors hover:border-accent hover:text-accent"
                    >
                      {sk.label}
                    </button>
                  ))}
                </div>
              )}
              {genPayload.result !== undefined && (
                <div className="flex flex-col gap-1.5 border-t border-surface-line pt-2">
                  <div className="text-uiSm text-ink-faint">结果 · {previewOfPayload(genPayload)}</div>
                  <GenResultEditor result={genPayload.result} onChange={(r) => applyResult(selNode.id, genPayload, r)} />
                </div>
              )}
              {/* 底部文字动作条（§4：扩展＝加文字项，不堆按钮块） */}
              <div className="flex shrink-0 items-center gap-2 text-ui">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 font-semibold text-accent hover:underline disabled:opacity-50"
                  disabled={generatingId === selNode.id}
                  onClick={() => void runGenerate(selNode.id, genPayload)}
                >
                  {generatingId === selNode.id ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                  {generatingId === selNode.id ? '生成中…' : '生成'}
                </button>
                <span className="text-ink-faint">·</span>
                <button
                  type="button"
                  className="text-ink-secondary hover:text-ink-primary hover:underline disabled:opacity-50"
                  disabled={generatingId === selNode.id}
                  onClick={() => void runOptimize(selNode.id, genPayload)}
                >
                  优化
                </button>
                {genPayload.capability === 'textToScript' && (
                  <>
                    <span className="text-ink-faint">·</span>
                    <button
                      type="button"
                      className="text-ink-secondary hover:text-ink-primary hover:underline"
                      onClick={() => fileRef.current?.click()}
                    >
                      上传
                    </button>
                  </>
                )}
                {genPayload.result !== undefined && (
                  <>
                    <span className="text-ink-faint">·</span>
                    <button
                      type="button"
                      className="text-ink-muted hover:text-state-danger hover:underline"
                      onClick={() => clearResult(selNode.id, genPayload)}
                    >
                      清空
                    </button>
                  </>
                )}
                {genPayload.result !== undefined && genPayload.capability === 'textToScript' && (
                  <>
                    <span className="text-ink-faint">·</span>
                    <button
                      type="button"
                      className="text-ink-secondary hover:text-ink-primary hover:underline"
                      onClick={() => void splitToStoryboard(selNode)}
                    >
                      拆分成导演剧本
                    </button>
                  </>
                )}
                {genPayload.result !== undefined && genPayload.capability === 'scriptToStoryboard' && (
                  <>
                    <span className="text-ink-faint">·</span>
                    <button
                      type="button"
                      className="text-ink-secondary hover:text-ink-primary hover:underline"
                      onClick={() => spawnSubjectCards(selNode)}
                    >
                      生成资产卡
                    </button>
                    <span className="text-ink-faint">·</span>
                    <button
                      type="button"
                      className="text-ink-secondary hover:text-ink-primary hover:underline"
                      onClick={() => explodeStoryboard(selNode)}
                    >
                      展开分镜
                    </button>
                  </>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".txt,.md,text/plain"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f !== undefined) {
                    onUploadScript(selNode.id, genPayload, f);
                  }
                  e.currentTarget.value = '';
                }}
              />
            </div>
          </FloatingPanel>
        )}
        {selImageNode !== undefined && anchor !== null && (
          <FloatingPanel
            key={selImageNode.id}
            title="分镜关键帧"
            x={anchor.x}
            y={anchor.y}
            onClose={() => setNodes((ns) => ns.map((n) => ({ ...n, selected: false })))}
          >
            <ImageNodePanel
              node={selImageNode}
              generating={generatingId === selImageNode.id}
              hasUpstream={doc.edges.some((e) => e.to === selImageNode.id)}
              onParams={(patch) => patchImageParams(selImageNode.id, patch)}
              onGenerate={() => void runGenerateImage(selImageNode)}
              onOptimize={() => void runOptimizeImage(selImageNode)}
              onCompose={() => runComposeFromUpstream(selImageNode)}
              onUpload={(f) => uploadImage(selImageNode, f)}
              onPickVariant={(m) => pickVariant(selImageNode.id, m)}
              onEdit={(op) => void runImageEdit(selImageNode, op)}
              onReverse={() => void runReversePrompt(selImageNode)}
              onUpscale={() => void runUpscale(selImageNode)}
              onCrop={(r) => void runCrop(selImageNode, r)}
              onMultiView={() => void runMultiView(selImageNode)}
              onRelight={(p) => void runRelight(selImageNode, p)}
              onDraw={() => openDraw(selImageNode)}
              onOpenInDirector={() => openInDirector(selImageNode.id)}
            />
          </FloatingPanel>
        )}
        {selNode !== undefined && selSubjectPayload !== undefined && anchor !== null && (
          <FloatingPanel
            key={selNode.id}
            title="主体资产"
            x={anchor.x}
            y={anchor.y}
            onClose={() => setNodes((ns) => ns.map((n) => ({ ...n, selected: false })))}
          >
            <SubjectNodePanel
              node={selNode}
              subject={selSubjectPayload}
              generating={generatingId === selNode.id}
              hasUpstream={true}
              onPatch={(patch) => patchSubject(selNode.id, patch)}
              onParams={(patch) => patchImageParams(selNode.id, patch)}
              onGenerate={() => void runGenerateImage(selNode)}
              onOptimize={() => void runOptimizeImage(selNode)}
              onCompose={() => composeSubjectPrompt(selNode)}
              onUpload={(f) => uploadImage(selNode, f)}
              onPickVariant={(m) => pickVariant(selNode.id, m)}
              onEdit={(op) => void runImageEdit(selNode, op)}
              onGenerateTurnaround={() => void generateSubjectTurnaround(selNode)}
            />
          </FloatingPanel>
        )}
        {selNode !== undefined && selShotPayload !== undefined && anchor !== null && (
          <FloatingPanel
            key={selNode.id}
            title="分镜"
            x={anchor.x}
            y={anchor.y}
            onClose={() => setNodes((ns) => ns.map((n) => ({ ...n, selected: false })))}
          >
            <ShotNodePanel
              payload={selShotPayload}
              onPatch={(patch: Partial<Omit<StoryboardShot, 'id' | 'index'>>) => patchShotNode(selNode.id, selShotPayload, patch)}
              onKeyframe={() => spawnKeyframeFromShot(selNode)}
              onAudio={() => spawnAudioFromShot(selNode)}
              onVideo={() => spawnVideoFromShot(selNode)}
            />
          </FloatingPanel>
        )}
        {selVideoNode !== undefined && anchor !== null && (
          <FloatingPanel
            key={selVideoNode.id}
            title="分镜视频"
            x={anchor.x}
            y={anchor.y}
            onClose={() => setNodes((ns) => ns.map((n) => ({ ...n, selected: false })))}
          >
            <VideoNodePanel
              node={selVideoNode}
              generating={generatingId === selVideoNode.id}
              onPrompt={(p) => patchVideoPrompt(selVideoNode.id, p)}
              onGenerate={() => void runGenerateVideo(selVideoNode)}
              onTool={(op) => void runVideoTool(selVideoNode, op)}
            />
          </FloatingPanel>
        )}
        {selAudioNode !== undefined && selAudioPayload !== undefined && anchor !== null && (
          <FloatingPanel
            key={selAudioNode.id}
            title="配音"
            x={anchor.x}
            y={anchor.y}
            onClose={() => setNodes((ns) => ns.map((n) => ({ ...n, selected: false })))}
          >
            <AudioNodePanel
              payload={selAudioPayload}
              generating={generatingId === selAudioNode.id}
              voiceNameFor={(role) => roleVoiceByName(role)?.name ?? '默认音色'}
              onPatchLine={(i, patch) => patchAudioLine(selAudioNode.id, selAudioPayload, i, patch)}
              onGenerateAll={() => void runGenerateAudio(selAudioNode)}
            />
          </FloatingPanel>
        )}
        <ReactFlow
          nodes={nodes}
          edges={visibleEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeDragStop={onNodeDragStop}
          onEdgesDelete={onEdgesDelete}
          onNodesDelete={onNodesDelete}
          onInit={(inst) => {
            rfRef.current = inst;
          }}
          onNodeContextMenu={onNodeContextMenu}
          onEdgeContextMenu={onEdgeContextMenu}
          onPaneContextMenu={onPaneContextMenu}
          onMoveStart={closeMenu}
          onMove={(_, vp) => setZoom(vp.zoom)}
          zoomOnDoubleClick={false}
          deleteKeyCode={['Backspace', 'Delete']}
          defaultEdgeOptions={{ interactionWidth: 24 }}
          nodesDraggable={!locked}
          elementsSelectable={!locked}
          zoomOnScroll={!locked}
          panOnDrag={!locked}
          fitView
          colorMode="dark"
          className={zoom < 0.55 ? 'zoom-far' : ''}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={24} size={1} />
          <MiniMap
            position="bottom-left"
            pannable
            zoomable
            nodeStrokeWidth={2}
            maskColor="rgba(18,17,16,0.72)"
            className="canvas-minimap"
          />
          <Panel position="bottom-center" className="!m-0">
            <div className="rounded-full border border-surface-line bg-surface-overlay px-2.5 py-0.5 text-uiSm font-num text-ink-secondary shadow-[0_2px_10px_rgba(0,0,0,.4)]">
              {Math.round(zoom * 100)}%
            </div>
          </Panel>
          <CanvasControls
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={doUndo}
            onRedo={doRedo}
            locked={locked}
            onToggleLock={() => setLocked((l) => !l)}
          />
        </ReactFlow>
      </div>

      {menu !== null && (
        <div
          ref={menuRef}
          className="fixed z-50 w-40 rounded-card border border-surface-edge bg-surface-overlay p-1 shadow-[0_6px_20px_rgba(0,0,0,.5)]"
          style={{ left: menu.x, top: menu.y }}
        >
          {menu.kind === 'node' &&
            (renaming ? (
              <div className="flex flex-col gap-1.5 p-1">
                <input
                  autoFocus
                  value={renameDraft}
                  maxLength={40}
                  className="w-full rounded-control border border-surface-edge bg-surface-base px-2 py-1 text-ui text-ink-primary outline-none focus:border-accent"
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitRename();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      closeMenu();
                    }
                  }}
                />
                <div className="flex justify-end gap-1">
                  <button type="button" className="btn-ghost px-2 py-0.5 text-uiSm" onClick={closeMenu}>
                    取消
                  </button>
                  <button type="button" className="btn-primary px-2 py-0.5 text-uiSm" onClick={commitRename}>
                    确定
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-0.5">
                <button type="button" className={menuItem} onClick={() => startRename(menu.id)}>
                  重命名
                </button>
                <button type="button" className={menuItem} onClick={doDuplicate}>
                  复制
                </button>
                <button type="button" className={menuItemDanger} onClick={doDeleteNode}>
                  删除
                </button>
              </div>
            ))}
          {menu.kind === 'edge' && (
            <div className="flex flex-col gap-0.5">
              <button type="button" className={menuItemDanger} onClick={doDeleteEdge}>
                删除连线
              </button>
            </div>
          )}
          {menu.kind === 'pane' && (
            <div className="flex flex-col gap-0.5">
              <div className="px-2 pb-0.5 pt-1 text-uiSm text-ink-faint">文本生成</div>
              <button type="button" className={menuItem} onClick={() => addTextAtPane('textToScript')}>
                文学剧本
              </button>
              <div className="my-1 h-px bg-surface-line" />
              <div className="px-2 pb-0.5 text-uiSm text-ink-faint">资产</div>
              <button type="button" className={menuItem} onClick={() => addSubjectAtPane('character')}>
                角色
              </button>
              <button type="button" className={menuItem} onClick={() => addSubjectAtPane('scene')}>
                场景
              </button>
              <button type="button" className={menuItem} onClick={() => addSubjectAtPane('prop')}>
                道具
              </button>
              <div className="my-1 h-px bg-surface-line" />
              <div className="px-2 pb-0.5 text-uiSm text-ink-faint">添加节点</div>
              {(Object.keys(NODE_KIND_LABELS) as NodeKind[]).filter((k) => k !== 'subject' && k !== 'shot' && k !== 'group').map((k) => (
                <button key={k} type="button" className={menuItem} onClick={() => addAtPane(k)}>
                  {NODE_KIND_LABELS[k]}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <TemplatesModal
        open={templatesOpen}
        templates={templates}
        onClose={() => setTemplatesOpen(false)}
        onImport={importTemplateById}
        onDelete={deleteTemplateById}
      />
      {drawTarget !== null && drawTarget.media !== undefined && (
        <DrawDialog
          open
          imageUrl={drawTarget.media.url}
          onClose={() => setDrawTarget(null)}
          onApply={(url) => runApplyDraw(drawTarget, url)}
        />
      )}
    </div>
  );
}
